import {
	formatDecimal,
	formatNumber,
	addCommasToNumberString,
} from '@dealstack/shared/lib/format/number';
import { HYPHEN_MINUS } from '@dealstack/frontend/shared/other/react/specialSigns';
import {
	HighPrecisionDecimal,
	MAX_SAFE_PRISMA_NUMBER,
} from '@dealstack/shared/lib/format/currency/highPrecisionDecimal';
import { CurrencyType } from '@dealstack/shared/lib/generated';

export const formatSummaryNumber = (value: number, returnNumericalZero = false): string => {
	return formatNumber({
		value,
		minPrecision: 2,
		maxPrecision: 2,
		lockPrecision: true,
		returnNumericalZero,
	});
};

// Monetary display precision: pad to 2dp (minPrecision), but do NOT round down at 2dp. The backend
// sends money as an exact 2dp fraction, so this formatter is display-only: commas + 2dp padding.
// Capping maxPrecision at 2 here would re-round and silently mask a backend value that is not 2dp;
// the high sentinel leaves well-formed values unchanged while surfacing any >2dp value loudly.
const MONEY_DISPLAY_MAX_PRECISION = 20;

export const formatSummaryDecimalString = (value: string, returnNumericalZero = false): string => {
	const sanitizedValue = value.replace(/,/g, '');

	return formatDecimal({
		value: new HighPrecisionDecimal(sanitizedValue),
		lockPrecision: true,
		minPrecision: 2,
		maxPrecision: MONEY_DISPLAY_MAX_PRECISION,
		returnNumericalZero,
	});
};

export const formatSummaryDecimal = (
	value: HighPrecisionDecimal | undefined,
	returnNumericalZero = false
): string => {
	if (value === undefined || value === null) {
		return '';
	}

	return formatDecimal({
		value,
		lockPrecision: true,
		minPrecision: 2,
		maxPrecision: MONEY_DISPLAY_MAX_PRECISION,
		returnNumericalZero,
	});
};

// Per-unit price (proceeds / units) is usually non-terminating and, unlike other money, is NOT
// pre-snapped to 2dp by the backend. Round to 2dp; prefix `~` only when that drops precision so an
// exact 2dp value the user typed stays un-tilded. Symbol sits inside the `~` (`~€0.02`, not `€~0.02`).
// WAT-731.
export const formatPerUnitMoney = (value: HighPrecisionDecimal, currencySymbol = ''): string => {
	const formatted = formatDecimal({
		value,
		lockPrecision: true,
		minPrecision: 2,
		maxPrecision: 2,
	});
	return `${value.isExact2dp() ? '' : '~'}${currencySymbol}${formatted}`;
};

export const formatMoicMetric = (value: HighPrecisionDecimal | undefined): string => {
	if (!value) {
		return '';
	}
	if (value.isNegative()) {
		throw new Error('Cannot have a negative moic');
	}
	if (value.equals(new HighPrecisionDecimal(MAX_SAFE_PRISMA_NUMBER))) {
		return 'Infinity';
	}

	if (value.isZero()) {
		return HYPHEN_MINUS;
	}

	return `${formatDecimal({
		value,
		lockPrecision: true,
		minPrecision: 2,
		maxPrecision: 2,
	})}x`;
};

export function formatPercentage(value: HighPrecisionDecimal | number | undefined | null): string {
	if (value === undefined || value === null) {
		return '';
	}
	const decimal = typeof value === 'number' ? new HighPrecisionDecimal(value) : value;

	if (decimal.isZero()) {
		return '-';
	}

	return `${addCommasToNumberString(decimal.toFixed(2))}%`;
}

export function formatSubColumnValue(value: number | undefined | null): string {
	if (value === undefined || value === null || value === 0) {
		return '';
	}
	return formatSummaryNumber(value);
}

export function formatValueInCurrency(
	value: HighPrecisionDecimal,
	currency: CurrencyType,
	multiplier: string
): string {
	const amount = formatSummaryDecimal(value, true);
	return `${currency} ${amount} ${multiplier}`;
}

export const maskString = (str: string | null | undefined = '', visibleChars = 4) => {
	const value = str ?? '';
	if (value.length <= visibleChars) {
		return value;
	}
	return `••••${value.slice(-visibleChars)}`;
};
